// Scroll choreography shared by the card animations and the 3D camera.
// Each <section data-stop="n"> is a camera keyframe; its [data-card] is the anchor.
// A section with data-hold keeps the camera on its keyframe for the section's whole height
// (its card is sticky), and holdProgress() reports how far through it the reader is.
// The camera shifts sideways to clear a card beside the room; a section with data-center
// (its card centered, or drawn onto an object in the room) keeps the room centered instead.

type Anchor = { top: number; left: number; w: number; h: number; hold: boolean; beside: boolean };

let anchors: Anchor[] = [];

export function measure(): Anchor[] {
  const A: Anchor[] = [];
  document.querySelectorAll<HTMLElement>('[data-stop]').forEach(sec => {
    const card = sec.querySelector<HTMLElement>('[data-card]'), c = card || sec;
    const op = c.offsetParent;
    const mr = op ? op.getBoundingClientRect() : { top: 0, left: 0 };
    const a = {
      top: mr.top + c.offsetTop, left: mr.left + c.offsetLeft, w: c.offsetWidth, h: c.offsetHeight,
      hold: 'hold' in sec.dataset, beside: !!card && !('center' in sec.dataset),
    };
    if (a.hold) { a.top = sec.getBoundingClientRect().top; a.h = sec.offsetHeight; }
    A[+sec.dataset.stop!] = a;
  });
  anchors = A;
  return A;
}

/** Scroll distance (px) until each stop is reached and until it is left; <= 0 means passed. */
function spans(A: Anchor[]) {
  const hd = document.querySelector('header');
  const hh = hd ? hd.getBoundingClientRect().bottom : 0;
  const vh = innerHeight;
  // A stop below where the page can scroll to counts as reached at the very bottom.
  const room = document.documentElement.scrollHeight - vh - scrollY;
  const arrive: number[] = [], leave: number[] = [];
  for (let i = 0; i < A.length; i++) {
    const a = A[i];
    if (!a) arrive[i] = leave[i] = 0;
    else if (a.hold) { arrive[i] = a.top - hh; leave[i] = a.top + a.h - vh; }
    else arrive[i] = leave[i] = a.top + Math.min(a.h, (vh - hh) * 0.85) / 2 - (vh + hh) / 2;
    arrive[i] = Math.min(arrive[i], room); leave[i] = Math.min(leave[i], room);
  }
  return { arrive, leave };
}

/** Fractional keyframe index for the current scroll position. */
export function stop(): number {
  const A = anchors.length ? anchors : measure();
  const n = A.length;
  if (!n) return 0;
  const { arrive, leave } = spans(A);
  if (arrive[0] > 0) return 0;
  for (let i = 0; i < n - 1; i++) {
    if (leave[i] >= 0) return i;
    if (arrive[i + 1] > 0) {
      const f = -leave[i] / Math.max(1, arrive[i + 1] - leave[i]);
      // Only a sliver of stillness at each end, so scrolling always moves the camera.
      const e = Math.max(0, Math.min(1, (f - 0.04) / 0.92));
      return i + e * e * (3 - 2 * e);
    }
  }
  return n - 1;
}

/** 0..1 progress through a data-hold stop. */
export function holdProgress(i: number): number {
  const A = anchors.length ? anchors : measure();
  if (!A[i]?.hold) return 0;
  const { arrive, leave } = spans(A);
  return Math.max(0, Math.min(1, -arrive[i] / Math.max(1, leave[i] - arrive[i])));
}

/** Maps hold progress to a sketchbook spread: `i` is the spread under the turning page, `flip` 0..1. */
export function spreadAt(p: number, n: number) {
  // The last page lies open just before the hold ends, so the book doesn't sit still at the end.
  const t = Math.max(0, Math.min(n - 1, p * ((n - 1) / 0.985) - 0.02));
  const i = Math.min(Math.floor(t), n - 2), f = t - i;
  const e = Math.max(0, Math.min(1, (f - 0.12) / 0.76));
  const flip = e * e * (3 - 2 * e);
  return { i, flip, current: flip < 0.5 ? i : i + 1 };
}

/** Document scroll position at which spread `j` of a data-hold stop lies open (inverse of spreadAt + holdProgress). */
export function spreadScrollY(i: number, j: number, n: number): number | null {
  const sec = document.querySelector<HTMLElement>(`[data-stop="${i}"][data-hold]`);
  if (!sec) return null;
  const hd = document.querySelector('header');
  const hh = hd ? hd.getBoundingClientRect().bottom : 0;
  const top = sec.getBoundingClientRect().top + scrollY;
  const p = (j + 0.02) / ((n - 1) / 0.985);
  return top - hh + p * (sec.offsetHeight - innerHeight + hh);
}

/** How far to shift the camera so the subject sits in the space the card leaves free. */
function free(i: number, w: number) {
  const a = anchors[i];
  if (!a?.beside) return { off: 0, fit: 1 };
  const r = a.left + a.w;
  const left = Math.max(0, a.left);
  const right = Math.max(0, w - r);
  const fw = Math.max(left, right);
  if (fw < 240) return { off: 0, fit: 1.2 };
  const c = left >= right ? left / 2 : r + right / 2;
  return { off: w / 2 - c, fit: Math.min(1.6, Math.max(1, (0.5 * w) / fw)) };
}

export function offsetFor(s: number, w: number) {
  const i = Math.floor(s), f = s - i;
  const a = free(i, w), b = f > 0 ? free(i + 1, w) : a;
  return { off: a.off + (b.off - a.off) * f, fit: a.fit + (b.fit - a.fit) * f };
}

/** Pops cards in from the side as they enter the viewport. A card with data-fold instead folds up
 * into the header's logo as the page scrolls away from it, and unfolds again on the way back. Returns a cleanup. */
export function startCardMotion(): () => void {
  let raf = 0, dirty = true;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Layout only moves on scroll, resize or reflow (fonts, images), so measure only then.
  const mark = () => { dirty = true; };
  const ro = new ResizeObserver(mark);
  ro.observe(document.body);
  document.querySelectorAll('[data-card]').forEach(el => ro.observe(el));
  addEventListener('scroll', mark, { passive: true });
  addEventListener('resize', mark);
  document.fonts?.addEventListener('loadingdone', mark);
  const tick = () => {
    raf = requestAnimationFrame(tick);
    if (!dirty) return;
    dirty = false;
    const vh = innerHeight;
    measure();
    const cards = [...document.querySelectorAll<HTMLElement>('[data-card]')];
    if (reduce) { cards.forEach(el => { el.style.transform = ''; el.style.opacity = '1'; }); return; }
    // Read every position before writing any style, so the browser lays out once per frame.
    const tops = cards.map(el => (el.offsetParent ? el.offsetParent.getBoundingClientRect().top : 0) + el.offsetTop);
    const hs = cards.map(el => el.offsetHeight);
    const logo = document.querySelector<HTMLElement>('.brand');
    const lr = logo?.getBoundingClientRect();
    cards.forEach((el, i) => {
      // A card pinned in place (data-still) rides in with its section instead.
      if ('still' in el.dataset) { el.style.transform = ''; el.style.opacity = '1'; return; }
      if ('fold' in el.dataset && lr) {
        const q = Math.max(0, Math.min(1, scrollY / (vh * 0.6)));
        const e = q * q * (3 - 2 * q);
        const left = (el.offsetParent ? el.offsetParent.getBoundingClientRect().left : 0) + el.offsetLeft;
        const sc = 1 + (lr.width / el.offsetWidth - 1) * e;
        el.style.transformOrigin = '0 0';
        el.style.transform = `translate3d(${(lr.left - left) * e}px,${(lr.top - tops[i]) * e}px,0) scale(${sc})`;
        el.style.opacity = String(1 - Math.max(0, Math.min(1, (q - 0.55) / 0.4)));
        // The logo's star turns as the card goes in.
        const mark = logo!.querySelector<HTMLElement>('.mark');
        if (mark) mark.style.transform = `rotate(${e * 144}deg)`;
        return;
      }
      // Done by the time the card is fully on screen, so a short card pinned to the bottom (the sketchbook on phones) still lands.
      const run = Math.max(1, Math.min(vh * 0.4, hs[i] + 32 - vh * 0.08));
      const t = Math.max(0, Math.min(1, (vh * 0.92 - tops[i]) / run));
      const e = 1 - Math.pow(1 - t, 3);
      const side = +(el.dataset.side || 0);
      el.style.transform = `translate3d(${(1 - e) * side * 60}px,${(1 - e) * 90}px,0) rotate(${(1 - e) * side * 4}deg)`;
      el.style.opacity = String(e);
    });
  };
  raf = requestAnimationFrame(tick);
  return () => {
    cancelAnimationFrame(raf);
    ro.disconnect();
    removeEventListener('scroll', mark);
    removeEventListener('resize', mark);
    document.fonts?.removeEventListener('loadingdone', mark);
  };
}

/** Document scroll position where stop `i` is in view: its card centered, or the start of a data-hold stop. */
export function stopScrollY(i: number): number | null {
  const A = measure();
  if (!A[i]) return null;
  if (i === 0) return 0;
  // A hold is reached once its top passes under the header; go on until its sticky rail has pinned.
  const hd = document.querySelector('header');
  return scrollY + spans(A).arrive[i] + (A[i].hold ? (hd ? hd.getBoundingClientRect().bottom : 0) + 2 : 0);
}

let wheelRaf = 0;
const endWheel = () => { cancelAnimationFrame(wheelRaf); wheelRaf = 0; };

/** True if the wheel would scroll something inside the page (a list, the nav bar) rather than the page. */
function scrollsInside(t: EventTarget | null, dy: number) {
  for (let el = t instanceof Element ? t : null; el && el !== document.body; el = el.parentElement) {
    const oy = getComputedStyle(el).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) {
      if (dy < 0 ? el.scrollTop > 0 : el.scrollTop + el.clientHeight < el.scrollHeight - 1) return true;
    }
  }
  return false;
}

/** Eases wheel scrolling: a mouse wheel moves in notches, which the cards, nav and sketchbook would visibly step through. */
export function startSmoothWheel(): () => void {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return () => {};
  // Tracked as a float of our own, since scrollY rounds and small steps would stall on it.
  let pos = 0, target = 0, last = 0;
  const step = (now: number) => {
    const dt = Math.min(0.05, (now - last) / 1000); last = now;
    pos += (target - pos) * (1 - Math.exp(-dt * 11));
    if (Math.abs(target - pos) < 0.5) pos = target;
    scrollTo({ top: pos, behavior: 'instant' });
    wheelRaf = pos === target ? 0 : requestAnimationFrame(step);
  };
  const onWheel = (e: WheelEvent) => {
    // The page holds still while the drone is being flown or the cube solved (where the wheel zooms).
    const cl = document.documentElement.classList;
    if (cl.contains('is-flying') || cl.contains('is-cube')) { e.preventDefault(); return; }
    if (e.defaultPrevented || e.ctrlKey || e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    if (document.documentElement.classList.contains('is-arcade') || scrollsInside(e.target, e.deltaY)) return;
    e.preventDefault();
    endGlide();
    // Start from wherever the page is now, if something else (keys, scrollbar, a glide) moved it since.
    if (!wheelRaf) { pos = target = scrollY; last = performance.now(); wheelRaf = requestAnimationFrame(step); }
    const dy = e.deltaY * (e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? innerHeight : 1);
    target = Math.max(0, Math.min(document.documentElement.scrollHeight - innerHeight, target + dy));
  };
  const stopEvents = ['pointerdown', 'touchstart', 'keydown'] as const;
  addEventListener('wheel', onWheel, { passive: false });
  stopEvents.forEach(e => addEventListener(e, endWheel, { passive: true }));
  return () => {
    endWheel();
    removeEventListener('wheel', onWheel);
    stopEvents.forEach(e => removeEventListener(e, endWheel));
  };
}

let glide = 0, endGlide = () => {};

/** Eased scroll to `y` that gives way to the reader's own scrolling. */
// `onDone` runs when the glide arrives, but not if the visitor interrupts it.
export function glideTo(y: number, onDone?: () => void) {
  endGlide();
  endWheel();
  const root = document.documentElement;
  const to = Math.round(Math.max(0, Math.min(root.scrollHeight - innerHeight, y)));
  const from = scrollY, d = to - from;
  if (Math.abs(d) < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) { scrollTo({ top: to, behavior: 'instant' }); onDone?.(); return; }
  const dur = Math.min(1700, 550 + Math.sqrt(Math.abs(d)) * 16);
  const t0 = performance.now();
  const stopEvents = ['wheel', 'touchstart', 'keydown'] as const;
  endGlide = () => {
    cancelAnimationFrame(glide);
    stopEvents.forEach(e => removeEventListener(e, endGlide));
    endGlide = () => {};
  };
  stopEvents.forEach(e => addEventListener(e, endGlide, { passive: true }));
  const step = (now: number) => {
    const t = Math.min(1, (now - t0) / dur);
    const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(2 - 2 * t, 3) / 2;
    scrollTo({ top: from + d * e, behavior: 'instant' });
    if (t < 1) glide = requestAnimationFrame(step); else { endGlide(); onDone?.(); }
  };
  glide = requestAnimationFrame(step);
}
